/**
 * StatusIndicator.jsx
 * Dot animado que indica status do veiculo
 *
 * Status:
 * - moving: Azul com pulse animation
 * - stopped/idle: Ambar sem animacao
 * - offline: Cinza/Vermelho sem animacao
 * - unknown: Cinza claro sem animacao
 * - alarm: Vermelho com pulse animation
 */

import { memo, useMemo } from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';

const useStyles = makeStyles()((theme, { color, shouldPulse, size }) => ({
  dot: {
    width: size,
    height: size,
    borderRadius: '50%',
    backgroundColor: color,
    boxShadow: shouldPulse ? `0 0 8px 2px ${color}` : 'none',
    animation: shouldPulse ? '$statusPulse 2s ease-in-out infinite' : 'none',
    flexShrink: 0,
    transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
  },
  // 2ECOMP Fase 7: Animação refinada - scale 1.3 como no protótipo
  '@keyframes statusPulse': {
    '0%, 100%': {
      transform: 'scale(1)',
      opacity: 1,
      boxShadow: `0 0 4px 0 ${color}`,
    },
    '50%': {
      transform: 'scale(1.3)',
      opacity: 0.8,
      boxShadow: `0 0 12px 3px ${color}`,
    },
  },
}));

const StatusIndicator = memo(({
  status,
  hasAlarm = false,
  size = 10,
  className,
}) => {
  const tokens = usePremiumTokens();

  const { color, shouldPulse } = useMemo(() => {
    // Alarme tem prioridade maxima
    if (hasAlarm) {
      return { color: tokens.status.alarm, shouldPulse: true };
    }

    // 4 estados visuais: Moving, Online, Offline, Ocioso (alinhado com StatusChipsBar)
    switch (status) {
      case 'moving':
        return { color: tokens.status.moving, shouldPulse: true };   // Azul - movimento
      case 'online':
      case 'idle':
      case 'stopped':
        return { color: tokens.status.online, shouldPulse: false };  // Verde - online
      case 'offline':
        return { color: tokens.status.offline, shouldPulse: false }; // Vermelho - desconectado
      case 'unknown':
      default:
        return { color: tokens.status.unknown, shouldPulse: false }; // Cinza - ocioso
    }
  }, [status, hasAlarm, tokens]);

  const { classes, cx } = useStyles({ color, shouldPulse, size });

  return <Box className={cx(classes.dot, className)} />;
});

StatusIndicator.displayName = 'StatusIndicator';
export default StatusIndicator;
